import { makeAutoObservable } from 'mobx'
import { type IIconsVector } from 'shared/ui'
import { type ICallType, type IOutcomeType } from 'entities/CallHistory/api/types'
import { type ICallFilter } from 'pages/conversations/pages/calls/ui/CallHistoryFilters/store/type'

export class CallFilter {
  private _id
  private _type: ICallType | 'all-calls'
  private _outcome: IOutcomeType | null
  private _source: 'ai_calling_agent' | null
  private _info: {
    title: string
    icon: IIconsVector
    iconColor: string
    tooltip?: string
    fontSize?: number
  }
  private _count = 0
  private _unreadCount = 0

  constructor(filter: ICallFilter) {
    makeAutoObservable(this)

    this._type = filter.type
    this._outcome = filter.outcome
    this._source = filter.source ?? null
    this._id = this._source
      ? `${this._type}.${this._source}`
      : `${this._type}${this._outcome ? `.${this._outcome}` : ''}`
    this._info = this.getInfo()
  }

  get id() {
    return this._id
  }

  get type() {
    return this._type
  }

  get outcome() {
    return this._outcome
  }

  get source() {
    return this._source
  }

  get info() {
    return this._info
  }

  get count() {
    return this._count
  }

  get unreadCount() {
    return this._unreadCount
  }

  setCount = (count: number) => {
    this._count = count
  }

  setUnreadCount = (count: number) => {
    this._unreadCount = count
  }

  increaseUnreadCount = () => {
    this._unreadCount += 1
  }

  decreaseUnreadCount = () => {
    this._unreadCount -= 1
  }

  reset = () => {
    this._count = 0
    this._unreadCount = 0
  }

  getInfo = (): typeof this._info => {
    if (this._type === 'inbound') {
      if (this._source === 'ai_calling_agent') {
        return {
          title: 'Handled by AI',
          icon: 'stars',
          fontSize: 14,
          iconColor: 'var(--purple-70)',
        }
      }

      if (this._outcome === 'answered') {
        return {
          title: 'Answered',
          icon: 'arrowLeftDown',
          iconColor: 'var(--content-primary-tertiary)',
        }
      }

      if (this._outcome === 'missed') {
        return {
          title: 'Missed',
          icon: 'missed',
          iconColor: 'var(--content-negative-primary)',
        }
      }

      if (this._outcome === 'voicemail') {
        return {
          title: 'Voicemail',
          icon: 'voicemail',
          iconColor: 'var(--content-primary-tertiary)',
        }
      }
    }

    if (this._type === 'outbound') {
      if (this._outcome === 'answered') {
        return {
          title: 'Answered',
          icon: 'arrowRightUp',
          iconColor: 'var(--content-primary-tertiary)',
        }
      }

      if (this._outcome === 'no_answer') {
        return {
          title: 'No answer',
          icon: 'noAnswer',
          iconColor: 'var(--content-negative-primary)',
        }
      }

      if (this._outcome === 'voicemail') {
        return {
          title: 'Voicemail',
          icon: 'voicemail',
          iconColor: 'var(--content-primary-tertiary)',
          tooltip: 'Shown only for transcribed calls',
        }
      }
    }

    return {
      title: 'All calls',
      icon: 'lines',
      iconColor: 'var(--content-primary-tertiary)',
    }
  }
}
